/**
 * BhiLayoutOrder — ред на модулите в BHI страницата.
 * `useBhiLayout` чете реда от /api/bhi/layout и връща карта id → CSS order.
 * `BhiSectionOrderPanel` е панелът за админ, с който редът се пренарежда.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LayoutList, ChevronUp, ChevronDown, RotateCcw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_INDEX = {
  intro: 0, assess: 1, observe: 2, understand: 3, plan: 4, act: 5, support: 6,
};

/** Картата id → CSS order се подава през контекст, защото модулите се
 *  рендерират от компонент вън от страницата. */
export const BhiOrderContext = React.createContext(null);

export function useBhiOrderStyle(id) {
  const map = React.useContext(BhiOrderContext);
  if (!map) return undefined;
  return { order: map[id] ?? 9990 };
}

export function useBhiLayout(module = "bhi") {
  const [data, setData] = useState(null);
  // iter1073 — докато редът не се знае, страницата не се показва: иначе
  // секциите се подреждат втори път и екранът трепка.
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/bhi/layout`, { params: { module } });
      setData(res);
    } catch { setData(null); } finally { setLoaded(true); }
  }, [module]);

  useEffect(() => { load(); }, [load]);

  const orderMap = React.useMemo(() => {
    if (!data?.order) return null;
    const map = {};
    Object.entries(data.order).forEach(([phase, ids]) => {
      const base = (PHASE_INDEX[phase] ?? 9) * 1000;
      map[`phase-${phase}`] = base;
      ids.forEach((id, i) => { map[id] = base + (i + 1) * 10; });
    });
    return map;
  }, [data]);

  return { layout: data, orderMap, reload: load, setLayout: setData, loaded };
}

export function BhiSectionOrderPanel({ isDark, layout, setLayout, style, module = "bhi" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!layout) return null;

  const move = async (phase, index, dir) => {
    const ph = layout.phases.find((p) => p.id === phase);
    const ids = ph.items.map((i) => i.id);
    const to = index + dir;
    if (to < 0 || to >= ids.length) return;
    [ids[index], ids[to]] = [ids[to], ids[index]];
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/bhi/layout`, { phase, items: ids, module });
      setLayout(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Записването не успя");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/bhi/layout`, { params: { module } });
      setLayout(data);
      toast.success("Върнат е редът по подразбиране");
    } catch { toast.error("Неуспешно"); } finally { setBusy(false); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-700" : "bg-white border-slate-200";

  return (
    <div style={style} className={`mb-4 rounded-2xl border ${card}`}
      data-testid="bhi-layout-admin">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 p-3 text-left"
        data-testid="bhi-layout-admin-toggle">
        <span className="text-[12px] font-extrabold inline-flex items-center gap-1.5">
          <LayoutList className="w-4 h-4 text-cyan-500" />
          Подреждане на модулите (само за админ)
          {layout.custom && (
            <span className="text-[10px] font-bold text-cyan-500">· променен ред</span>
          )}
        </span>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" />
              : <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && (
        <div className="px-3 pb-3" data-testid="bhi-layout-admin-body">
          <div className="text-[11px] opacity-70 mb-2">{layout.note_bg}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {layout.phases.map((p) => (
              <div key={p.id} className={`p-2.5 rounded-xl border ${
                isDark ? "border-white/10" : "border-slate-200"}`}
                data-testid={`bhi-layout-phase-${p.id}`}>
                <div className="text-[10px] font-bold uppercase opacity-60 mb-1">
                  {p.label_bg}
                </div>
                <div className="space-y-1">
                  {p.items.map((it, i) => (
                    <div key={it.id} className="flex items-center gap-1.5">
                      <span className="flex-1 min-w-0 truncate text-[11px]">
                        {i + 1}. {it.label_bg}
                      </span>
                      <button disabled={busy || i === 0} onClick={() => move(p.id, i, -1)}
                        className="p-1 rounded hover:bg-cyan-500/10 disabled:opacity-25"
                        data-testid={`bhi-layout-up-${it.id}`}>
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button disabled={busy || i === p.items.length - 1}
                        onClick={() => move(p.id, i, 1)}
                        className="p-1 rounded hover:bg-cyan-500/10 disabled:opacity-25"
                        data-testid={`bhi-layout-down-${it.id}`}>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button onClick={reset} disabled={busy}
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold
                       text-cyan-500 hover:underline disabled:opacity-40"
            data-testid="bhi-layout-reset">
            <RotateCcw className="w-3.5 h-3.5" /> Върни реда по подразбиране
          </button>
        </div>
      )}
    </div>
  );
}
